// src/v2/hooks/useReportR3.js
import { useState, useCallback } from 'react';
import reportsService from '../services/reportsService';
import { useGeneralMessage } from '../../common/APICommon';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export const useReportR3 = () => {
  const [loading, setLoading] = useState(false);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  /**
   * Scenario A: Download the raw SSS template directly from public/docs/
   */
  const downloadBlankTemplate = useCallback((month, year) => {
    const pdfUrl = `${window.location.origin}/docs/SSS_R3.pdf`;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `SSS_R3_${month}_${year}_Blank.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showGeneralMessage("Blank template downloaded (No records found).", true);
  }, [showGeneralMessage]);

  /**
   * Scenario B: Fill the SSS R-3 with data using pdf-lib
   */
  const fillAndDownloadPDF = useCallback(async (data, month, year, companyInfo) => {
    try {
      const pdfUrl = `${window.location.origin}/docs/SSS_R3.pdf`;
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("Template not found");

      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const page = pdfDoc.getPages()[0];
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // --- COORDINATE MAPPING (Estimates for SSS R-3) ---
      // 1. Employer Details
      page.drawText(companyInfo?.name || "", { x: 80, y: 700, size: 9, font });
      page.drawText(companyInfo?.sssId || "", { x: 450, y: 700, size: 9, font });

      // 2. Applicable Period
      page.drawText(`${month} ${year}`, { x: 450, y: 730, size: 10, font });

      // 3. Employee Rows (Simplified loop)
      let currentY = 580;
      data.slice(0, 15).forEach((item) => {
        page.drawText(item.ssNumber || "", { x: 60, y: currentY, size: 8, font });
        page.drawText(item.fullName || "", { x: 140, y: currentY, size: 8, font });
        page.drawText((item.ssContribution || 0).toLocaleString(), { x: 380, y: currentY, size: 8, font });
        page.drawText((item.ecContribution || 0).toLocaleString(), { x: 480, y: currentY, size: 8, font });
        currentY -= 14;
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `SSS_R3_${month}_${year}.pdf`;
      link.click();

      showGeneralMessage("SSS R-3 generated and downloaded.", true);
    } catch (error) {
      console.error(error);
      showGeneralMessage("Error filling PDF. Downloading blank instead.", false);
      downloadBlankTemplate(month, year);
    }
  }, [showGeneralMessage, downloadBlankTemplate]);

  const generateAndDownload = useCallback(async (params, month, year, companyInfo) => {
    setLoading(true);
    try {
      let data = [];
      try {
        const res = await reportsService.getReportData('R3', params);
        data = res || [];
      } catch (apiErr) {
        console.warn("Backend not found. Falling back to Demo Mode.");
        data = [];
      }

      if (data.length === 0) {
        downloadBlankTemplate(month, year);
      } else {
        await fillAndDownloadPDF(data, month, year, companyInfo);
      }
    } catch (err) {
      showGeneralMessage("Failed to process request.", false);
    } finally {
      setLoading(false);
    }
  }, [fillAndDownloadPDF, downloadBlankTemplate, showGeneralMessage]);

  return { loading, generateAndDownload, showMessage, message, isSuccess };
};